import React from 'react';

import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import { spacing } from '~/src/dash/publicaUI';
import {
  IPlatformConnectionWithNamesAndType,
  orderDirection,
} from '~/src/dash/views/platform-connect/common';
import {
  CPMOpenDrawerRender,
  EllipsisWithTooltipAndOpenDrawerRender,
  ImpressionsOpenDrawerRender,
  NotesSidebarLink,
  PlatformConnectTypeDisplay,
} from '~/src/dash/views/platform-connect/common';

export const formatDataToLVTRows = (
  data: IPlatformConnectionWithNamesAndType[],
  cfg: IPendingOffersTable,
): IListViewTableRow[] => {
  const rows = [] as IListViewTableRow[];
  data.forEach((datum) => {
    const row = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = datum[responseKey as keyof IPlatformConnectionWithNamesAndType];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      row[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    rows.push(row);
  });
  return rows;
};

interface IPendingOffersTable {
  columns: IListViewTableColumns;
}

const CustomStatusLink = (props: ICustomRenderCellJSXProps) => {
  const { customFunctions } = useListViewTableStore();
  return (
    <div
      onClick={() => customFunctions.handleClickOpenDrawer(props)}
      style={{
        cursor: 'pointer',
      }}
    >
      {customFunctions.handleRenderStatus(props)}
    </div>
  );
};

export interface IPendingOffersConfig {
  cypressClassName: string;
  defaultState: {
    activeColumnKeys: string[];
    isTableExpanded: boolean;
    orderDirection: orderDirection;
    orderBy: string;
    page: number;
    rowsPerPage: RowsPerPage;
  };
  columnSelector: {
    resetKeys?: string[];
  };
  table: IPendingOffersTable;
}

export const pendingOffersViewConfig = {
  cypressClassName: 'PlatformConnectPendingOffers',
  defaultState: {
    activeColumnKeys: ['CustomLeftColumn', 'Name', 'Type', 'Impressions', 'ProposedCPM', 'Notes'],
    isTableExpanded: false,
    orderDirection: 'desc',
    orderBy: 'Partner',
    page: 0,
    rowsPerPage: 25,
  },
  columnSelector: {
    resetKeys: ['CustomLeftColumn'],
  },
  table: {
    columns: {
      CreatedAt: {
        isHidden: true,
        isSearchable: true,
        key: 'CreatedAt',
        label: 'Created At',
        requestKey: '',
        responseKey: 'CreatedAt',
        valueType: 'string',
      },
      ID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: 'ID',
        responseKey: 'ID',
        valueType: 'string',
      },
      BuyerPublisherID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'BuyerPublisherID',
        label: 'Buyer Publisher ID',
        requestKey: '',
        responseKey: 'BuyerPublisherID',
        valueType: 'string',
      },
      SellerPublisherID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'SellerPublisherID',
        label: 'Seller Publisher ID',
        requestKey: '',
        responseKey: 'SellerPublisherID',
        valueType: 'string',
      },
      ActionPublisherID: {
        formatValue: (value: number) => String(value),
        isHidden: true,
        isSearchable: true,
        key: 'ActionPublisherID',
        label: 'Action Publisher ID',
        requestKey: '',
        responseKey: 'ActionPublisherID',
        valueType: 'string',
      },
      DemandSource: {
        isHidden: true,
        isSearchable: true,
        key: 'DemandSource',
        label: 'Demand Source',
        requestKey: '',
        responseKey: 'DemandSource',
        valueType: 'string',
      },
      AudienceSegmentID: {
        isHidden: true,
        isSearchable: true,
        key: 'AudienceSegmentID',
        label: 'Audience Segment ID',
        requestKey: '',
        responseKey: 'AudienceSegmentID',
        valueType: 'string',
      },
      DealID: {
        isHidden: true,
        isSearchable: true,
        key: 'DealID',
        label: 'Deal ID',
        requestKey: '',
        responseKey: 'DealID',
        valueType: 'string',
      },
      PlatformConnectionOfferHistory: {
        isHidden: true,
        isSearchable: true,
        key: 'PlatformConnectionOfferHistory',
        label: 'Platform Connection Offer History',
        requestKey: '',
        responseKey: 'PlatformConnectionOfferHistory',
        valueType: 'string',
      },
      CustomLeftColumn: {
        cellJSX: CustomStatusLink,
        cellRenderType: 'name_link',
        cellStyle: {
          verticalAlign: 'middle',
          height: spacing.huge,
        },
        disableSort: true,
        defaultOrderDirection: 'asc',
        headerRenderType: 'left_column',
        isSearchable: true,
        key: 'CustomLeftColumn',
        label: 'Status',
        requestKey: '',
        responseKey: 'Status',
        valueType: 'string',
      },
      Name: {
        cellJSX: EllipsisWithTooltipAndOpenDrawerRender,
        cellRenderType: 'actions',
        cellStyle: {
          fontWeight: FONT_WEIGHT.base,
          verticalAlign: 'middle',
        },
        disableSort: true,
        defaultOrderDirection: 'asc',
        headerRenderType: 'name',
        isSearchable: true,
        key: 'Name',
        label: 'Partner',
        requestKey: '',
        responseKey: 'PartnerPublisherName',
        valueType: 'string',
      },
      Type: {
        cellJSX: PlatformConnectTypeDisplay,
        cellRenderType: 'name_link',
        cellStyle: {
          verticalAlign: 'middle',
          paddingRight: '8px',
        },
        headerStyle: {
          paddingLeft: '8px',
        },
        disableSort: true,
        defaultOrderDirection: 'asc',
        isSearchable: true,
        key: 'Type',
        label: 'Type',
        requestKey: '',
        responseKey: 'Type',
        valueType: 'string',
      },
      Impressions: {
        cellJSX: ImpressionsOpenDrawerRender,
        cellStyle: {
          verticalAlign: 'middle',
          paddingRight: '0px',
        },
        disableSort: true,
        defaultOrderDirection: 'asc',
        headerRenderType: 'number',
        isSearchable: true,
        key: 'Impressions',
        label: 'Impressions',
        requestKey: '',
        responseKey: 'ImpressionsGoal',
        valueType: 'number',
      },
      ProposedCPM: {
        cellJSX: CPMOpenDrawerRender,
        cellStyle: {
          verticalAlign: 'middle',
          paddingRight: '0px',
        },
        disableSort: true,
        defaultOrderDirection: 'asc',
        headerRenderType: 'number',
        isSearchable: true,
        key: 'ProposedCPM',
        label: 'Proposed CPM',
        requestKey: '',
        responseKey: 'BidFloor',
        valueType: 'number',
      },
      Notes: {
        cellJSX: NotesSidebarLink,
        cellStyle: {
          verticalAlign: 'middle',
          paddingLeft: '26px',
        },
        disableSort: true,
        headerRenderType: 'centered_text',
        key: 'Notes',
        label: 'Notes',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
    },
  },
} as IPendingOffersConfig;
